import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import deepEqual from 'lodash.isequal';
import omit from 'lodash.omit';

import { areAddressesSemanticallyEqual } from '../../../../../../address/address-helpers';

import { emptyObject } from 'ui-components/default-props/empty';

import Global from '../../../../../../store/global-selectors';
import { FulfillmentPlanOrderGridStatus, EligibleToEditStatuses } from '../../../../../../enums/fulfillmentplan-order-grid-status';
import {
  useGetFulfillmentPlanIdsFromSalesOrderIdWithEligibleStatus,
  useEntitiesFulfillmentPlansFromSalesOrderId,
} from '../../../../../../hooks/entities/selectors/fulfillment-plans';
import {
  isAddressInErrorOrWarningState,
  isAddressUnVerified,
  isAddressVerified,
} from '../../../../../../components/address-validation/validate-address-helpers';
import { isEmptyAddress } from '../../../../../../address/address-fields/helpers';
import { useHasFulfillmentPlanRestriction } from '../../../restrictions-context';
import { UiRestrictions } from '../../../../../../enums/component-states';
import { useIsFulfillmentPlanEditable } from '../use-shipment-details';

export const useIsFulfillmentPlanShipped = (fulfillmentPlanId) => {
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const { orderGridStatus = '' } = fulfillmentPlan;
  return (
    orderGridStatus === FulfillmentPlanOrderGridStatus.Shipped.name ||
    orderGridStatus === FulfillmentPlanOrderGridStatus.PendingFulfillment.name ||
    orderGridStatus === FulfillmentPlanOrderGridStatus.PendingPickup.name
  );
};

export const useGetShipTo = (fulfillmentPlanId) => {
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const { shipTo = emptyObject } = fulfillmentPlan;
  return shipTo;
};

export const useGetRecipientInformation = (fulfillmentPlanId) => {
  const fulfillmentPlan = useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));
  const { recipientInfo = emptyObject } = fulfillmentPlan;
  return recipientInfo;
};

export const useGetMarketplaceShipTo = (salesOrderId) => {
  return useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'marketplaceShipTos.0',
    }),
  );
};

export const useGetAllFulfillmentPlanShipTosFromSalesOrder = (salesOrderId) => {
  const fulfillmentPlans = useEntitiesFulfillmentPlansFromSalesOrderId(salesOrderId);
  const fulfillmentPlanShipTos = fulfillmentPlans.map((fulfillmentPlan) => fulfillmentPlan?.shipTo).filter((shipTo) => shipTo);

  return fulfillmentPlanShipTos;
};

// not for comparing actual addresses, just for comparing if user facing shipTo fields are equal
export const useGetAllFulfillmentPlanShipTosAreEqualFromSalesOrder = (salesOrderId) => {
  const fulfillmentPlanShipTos = useGetAllFulfillmentPlanShipTosFromSalesOrder(salesOrderId);
  const excludedProperties = useMemo(
    () => ['company', 'lockAddress', 'residentialIndicator', 'verificationMessage', 'verificationStatus', 'verificationUtc', 'isModified'],
    [],
  );

  const allShipTosEqual = useMemo(() => {
    const firstShipTo = omit(fulfillmentPlanShipTos[0] || emptyObject, excludedProperties);
    return fulfillmentPlanShipTos.every((fulfillmentPlanShipTo) => deepEqual(omit(fulfillmentPlanShipTo, excludedProperties), firstShipTo));
  }, [fulfillmentPlanShipTos, excludedProperties]);

  return allShipTosEqual;
};

export const useGetAddressValidationLink = (fulfillmentPlanId, salesOrderId) => {
  const fulfillmentPlanShipTo = useGetShipTo(fulfillmentPlanId);
  // Use marketplace address (static original) for comparison to avoid race conditions
  const marketplaceShipTo = useGetMarketplaceShipTo(salesOrderId) ?? emptyObject;
  const doesFpShipToMatchMarketplace = areAddressesSemanticallyEqual(fulfillmentPlanShipTo, marketplaceShipTo);
  const isErrorOrWarningState = isAddressInErrorOrWarningState(fulfillmentPlanShipTo.verificationStatus);
  const isUnVerified = isAddressUnVerified(fulfillmentPlanShipTo.verificationStatus);
  const isVerified = isAddressVerified(fulfillmentPlanShipTo.verificationStatus);
  const isShippedStatus = useIsFulfillmentPlanShipped(fulfillmentPlanId);
  return {
    marketplaceShipTo,
    fulfillmentPlanShipTo,
    isUnVerified,
    isErrorOrWarningState,
    isShippedStatus,
    doesFpShipToMatchMarketplace,
    isVerified,
  };
};

export const useGetAddressValidationStatus = (fulfillmentPlanId) => {
  const fulfillmentPlanShipTo = useGetShipTo(fulfillmentPlanId);
  const isEmpty = isEmptyAddress(fulfillmentPlanShipTo);
  return {
    isEmptyAddress: isEmpty,
    countryCode: fulfillmentPlanShipTo.countryCode || '',
    residentialIndicator: fulfillmentPlanShipTo.residentialIndicator || '',
    validation: fulfillmentPlanShipTo.verificationStatus || '',
    validationMessage: fulfillmentPlanShipTo.verificationMessage || '',
  };
};

export const useGetReadOnlyAddress = (fulfillmentPlanId) => {
  const fulfillmentPlanShipTo = useGetShipTo(fulfillmentPlanId);
  const fulfillmentPlanRecipientInfo = useGetRecipientInformation(fulfillmentPlanId);
  return {
    fulfillmentPlanShipTo,
    fulfillmentPlanRecipientInfo,
  };
};

export const useGetUpdatableFulfillmentPlanIds = (salesOrderId) => {
  return useGetFulfillmentPlanIdsFromSalesOrderIdWithEligibleStatus(salesOrderId, EligibleToEditStatuses);
};

export const useIsFulfillmentPlanShipToUpdatable = (fulfillmentPlanId) => {
  const isEligibleStatus = useIsFulfillmentPlanEditable(fulfillmentPlanId);
  const isRestricted = useHasFulfillmentPlanRestriction(UiRestrictions.ShipTos);
  return isEligibleStatus && !isRestricted;
};
